import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { BadgePlus, SquarePen } from 'lucide-react';
import {
  SupplierDTO,
  useCreateSupplier,
  useUpdateSupplier,
} from '@/hooks/useSuppliers';
import { PaymentMethod } from '@/api/suppliers';
import LoadingCircle from '@/components/LoadingCircle';
import { SupplierFormValues, supplierFormSchema } from '@/schemas/supplier';

interface CreateUpdateProps {
  supplier?: SupplierDTO;
}

const CreateUpdate = ({ supplier }: CreateUpdateProps) => {
  const [open, setOpen] = useState(false);
  const isEdit = !!supplier;

  const { mutateAsync: createSupplier, isPending } = useCreateSupplier();
  const { mutateAsync: updateSupplier, isPending: isUpdating } =
    useUpdateSupplier();

  const form = useForm<SupplierFormValues>({
    resolver: zodResolver(supplierFormSchema),
    defaultValues: {
      name: supplier?.name || '',
      address: supplier?.address || '',
      taxCode: parseInt(supplier?.taxCode || '0'),
      phone: supplier?.phone || '',
      email: supplier?.email || '',
      bankName: supplier?.bankName || '',
      bankAccountNumber: supplier?.bankAccountNumber || '',
      bankAccountHolder: supplier?.bankAccountHolder || '',
      paymentMethod: supplier?.paymentMethod || '',
    },
    mode: 'all',
  });

  // Watch paymentMethod field for changes
  const paymentMethod = form.watch('paymentMethod');
  const bankName = form.watch('bankName');
  const bankAccountNumber = form.watch('bankAccountNumber');
  const bankAccountHolder = form.watch('bankAccountHolder');

  // Clear bank fields when paymentMethod is not BANK_TRANSFER
  useEffect(() => {
    if (paymentMethod && paymentMethod !== PaymentMethod.BANK_TRANSFER) {
      form.setValue('bankName', '');
      form.setValue('bankAccountNumber', '');
      form.setValue('bankAccountHolder', '');
      // Clear any existing errors for bank fields
      form.clearErrors(['bankName', 'bankAccountNumber', 'bankAccountHolder']);
    }
  }, [paymentMethod, form]);

  // Trigger validation for bank fields when paymentMethod or their values change
  useEffect(() => {
    if (paymentMethod === PaymentMethod.BANK_TRANSFER) {
      if (bankName !== undefined) {
        form.trigger('bankName');
      }
      if (bankAccountNumber !== undefined) {
        form.trigger('bankAccountNumber');
      }
      if (bankAccountHolder !== undefined) {
        form.trigger('bankAccountHolder');
      }
    }
  }, [paymentMethod, bankName, bankAccountNumber, bankAccountHolder, form]);

  useEffect(() => {
    if (supplier) {
      form.reset({
        name: supplier.name,
        address: supplier.address,
        taxCode: parseInt(supplier.taxCode) || 0,
        phone: supplier.phone,
        email: supplier.email,
        bankName: supplier.bankName || '',
        bankAccountNumber: supplier.bankAccountNumber || '',
        bankAccountHolder: supplier.bankAccountHolder || '',
        paymentMethod: supplier.paymentMethod || '',
      });
    }
  }, [supplier, form]);

  async function onSubmit(values: SupplierFormValues) {
    const dataPayload = {
      ...values,
      bankName: values.bankName || null,
      bankAccountNumber: values.bankAccountNumber || null,
      bankAccountHolder: values.bankAccountHolder || null,
      paymentMethod: values.paymentMethod || null,
      taxCode: values.taxCode.toString(),
    };
    if (isEdit) {
      await updateSupplier({
        id: supplier.id.toString(),
        data: dataPayload,
      });
    } else {
      await createSupplier(dataPayload);
    }
    setOpen(false);
    form.reset();
  }

  // Check if bank fields should be disabled
  const isBankFieldsDisabled = paymentMethod !== PaymentMethod.BANK_TRANSFER;

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        form.reset();
        setOpen(value);
      }}
    >
      <DialogTrigger asChild>
        <Button
          variant={isEdit ? 'outline' : 'default'}
          className={isEdit ? 'luxury-button-outline' : 'luxury-button-primary'}
        >
          {isEdit ? (
            <>
              <SquarePen className="w-4 h-4" />
              Sửa
            </>
          ) : (
            <>
              <BadgePlus className="w-4 h-4" />
              Thêm mới
            </>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="min-w-[800px] max-h-[95vh] overflow-y-auto luxury-theme">
        <DialogDescription className="sr-only">
          {isEdit ? 'Sửa nhà cung cấp' : 'Thêm nhà cung cấp'}
        </DialogDescription>
        <DialogHeader className="luxury-header">
          <DialogTitle className="luxury-title">
            {isEdit ? 'Cập nhật nhà cung cấp' : 'Tạo nhà cung cấp mới'}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <div className="luxury-card">
              <h3 className="luxury-subtitle mb-4">Thông tin nhà cung cấp</h3>
              <div className="flex flex-col gap-6 items-start">
                <div className="flex gap-6 w-full items-start">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem className="w-1/2">
                        <Label required className="luxury-label">
                          Tên nhà cung cấp
                        </Label>
                        <FormControl>
                          <Input {...field} className="luxury-input" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="taxCode"
                    render={({ field }) => (
                      <FormItem className="w-1/2">
                        <Label required className="luxury-label">
                          Mã số thuế
                        </Label>
                        <FormControl>
                          <Input
                            {...field}
                            value={field.value || ''}
                            onChange={(e) =>
                              field.onChange(parseInt(e.target.value))
                            }
                            className="luxury-input"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="flex gap-6 w-full items-start">
                  <FormField
                    control={form.control}
                    name="address"
                    render={({ field }) => (
                      <FormItem className="w-1/2">
                        <Label required className="luxury-label">
                          Địa chỉ
                        </Label>
                        <FormControl>
                          <Input {...field} className="luxury-input" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem className="w-1/2">
                        <Label required className="luxury-label">
                          Số điện thoại
                        </Label>
                        <FormControl>
                          <Input {...field} className="luxury-input" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="flex gap-6 w-full items-start">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem className="w-1/2">
                        <Label required className="luxury-label">
                          Email
                        </Label>
                        <FormControl>
                          <Input
                            {...field}
                            type="email"
                            className="luxury-input"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="paymentMethod"
                    render={({ field }) => (
                      <FormItem className="w-1/2">
                        <Label className="luxury-label">
                          Phương thức thanh toán
                        </Label>
                        <FormControl>
                          <Select
                            value={field.value || ''}
                            onValueChange={field.onChange}
                          >
                            <SelectTrigger className="w-full luxury-input">
                              <SelectValue placeholder="Chọn phương thức thanh toán" />
                            </SelectTrigger>
                            <SelectContent className="border-luxury-200">
                              <SelectItem value={PaymentMethod.BANK_TRANSFER}>
                                Chuyển khoản
                              </SelectItem>
                              <SelectItem value={PaymentMethod.CASH}>
                                Tiền mặt
                              </SelectItem>
                              <SelectItem value={PaymentMethod.CREDIT}>
                                Tín dụng
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </div>

            <div className="luxury-card">
              <h3 className="luxury-subtitle mb-4">Thông tin ngân hàng</h3>
              <div className="flex flex-col gap-6 items-start">
                <div className="flex gap-6 w-full items-start">
                  <FormField
                    control={form.control}
                    name="bankName"
                    render={({ field }) => (
                      <FormItem className="w-1/2">
                        <Label
                          {...(paymentMethod ===
                            PaymentMethod.BANK_TRANSFER && {
                            required: true,
                          })}
                          className="luxury-label"
                        >
                          Tên ngân hàng
                        </Label>
                        <FormControl>
                          <Input
                            {...field}
                            className="luxury-input"
                            value={field.value || ''}
                            disabled={isBankFieldsDisabled}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="bankAccountNumber"
                    render={({ field }) => (
                      <FormItem className="w-1/2">
                        <Label
                          {...(paymentMethod ===
                            PaymentMethod.BANK_TRANSFER && {
                            required: true,
                          })}
                          className="luxury-label"
                        >
                          Số tài khoản
                        </Label>
                        <FormControl>
                          <Input
                            {...field}
                            className="luxury-input"
                            value={field.value || ''}
                            disabled={isBankFieldsDisabled}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="w-full">
                  <FormField
                    control={form.control}
                    name="bankAccountHolder"
                    render={({ field }) => (
                      <FormItem>
                        <Label
                          {...(paymentMethod ===
                            PaymentMethod.BANK_TRANSFER && {
                            required: true,
                          })}
                          className="luxury-label"
                        >
                          Chủ tài khoản
                        </Label>
                        <FormControl>
                          <Input
                            {...field}
                            className="luxury-input"
                            value={field.value || ''}
                            disabled={isBankFieldsDisabled}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full mt-6 luxury-button-primary"
              disabled={isPending || isUpdating}
            >
              {isPending || isUpdating ? (
                <LoadingCircle />
              ) : (
                <>{isEdit ? 'Cập nhật nhà cung cấp' : 'Tạo nhà cung cấp mới'}</>
              )}
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateUpdate;
